import React, { useMemo, useState } from "react";
import { useGame, usePlayer, usePlayers } from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import { useRandomizedQuizQuestions } from "../components/useRandomizedQuizQuestions";
import { formatCents, formatDollars, getTrackingRulesParams } from "../tracking/trackingParams";
import { getCounterpartCopy, isLossFrameDesign } from "../payoffFrame";
import { usesDisclosedAssignedDifficulty } from "../../../shared/experimentBehavior.js";

function getQuizQuestions(lossFrame, counterpartLabel, showStandardDifficultyUpdate) {
  const questions = [
    {
      id: "sameGame",
      prompt: "What should you do during Game 2?",
      options: [
        {
          value: "correct",
          label: "Keep your mouse inside the moving circle, as in Game 1.",
        },
        {
          value: "faster",
          label: "Keep your mouse outside the moving circle.",
        },
        {
          value: "click",
          label: "Click the moving circle as many times as possible.",
        },
      ],
      correctValue: "correct",
    },
    {
      id: "negotiatedPayments",
      prompt: lossFrame
        ? "How are individual losses from the Game 2 saved money determined?"
        : "How are individual payments from the Game 2 pot determined?",
      options: lossFrame
        ? [
            {
              value: "time",
              label: "Each person's loss is determined automatically by their own tracking time.",
            },
            {
              value: "correct",
              label: `You and your ${counterpartLabel} negotiate how much saved money is taken from each person.`,
            },
            {
              value: "winner",
              label: "Whoever keeps their cursor in the circle longer automatically avoids all losses.",
            },
          ]
        : [
            {
              value: "time",
              label: "Each person automatically gets paid based only on their own tracking time.",
            },
            {
              value: "correct",
              label: `You and your ${counterpartLabel} negotiate how to split the Game 2 pot.`,
            },
            {
              value: "winner",
              label: "Whoever keeps their cursor in the circle longer automatically gets the whole pot.",
            },
          ],
      correctValue: "correct",
    },
  ];

  if (showStandardDifficultyUpdate) {
    questions.push({
      id: "sameDifficulty",
      prompt: `What difficulty (circle size) will you and your ${counterpartLabel} have in Game 2?`,
      options: [
        {
          value: "swapped",
          label: "We swap difficulties from Game 1.",
        },
        {
          value: "correct",
          label: "We both have standard difficulty.",
        },
        {
          value: "unchanged",
          label: "We keep our Game 1 difficulty.",
        },
      ],
      correctValue: "correct",
    });
  }

  return questions;
}

export function Stage2GameRulesStage() {
  const game = useGame();
  const player = usePlayer();
  const players = usePlayers();
  const [currentPage, setCurrentPage] = useState(
    player.stage.get("stage2RulesPage") === "quiz" ? "quiz" : "rules"
  );
  const [answers, setAnswers] = useState(
    player.stage.get("stage2RulesQuizAnswers") ?? {}
  );
  const [submittedAnswers, setSubmittedAnswers] = useState(
    player.stage.get("stage2RulesQuizSubmittedAnswers") ?? {}
  );
  const [attemptedQuiz, setAttemptedQuiz] = useState(
    player.stage.get("stage2RulesQuizAttempted") === true
  );

  const allSubmitted = useMemo(
    () => players.every((p) => p.stage.get("submit")),
    [players]
  );
  const design = game.get("design") || {};
  const lossFrame = isLossFrameDesign(design);
  const counterpart = getCounterpartCopy(design);
  const showStandardDifficultyUpdate =
    usesDisclosedAssignedDifficulty(design) &&
    design.loserConditionGetsWinnerCircleInStage2 === true;
  const questions = useMemo(
    () =>
      getQuizQuestions(lossFrame, counterpart.term, showStandardDifficultyUpdate),
    [counterpart.term, lossFrame, showStandardDifficultyUpdate]
  );
  const quizQuestions = useRandomizedQuizQuestions(
    questions, player.stage, "stage2RulesQuizOptionOrder"
  );
  const incorrectQuestionIds = useMemo(
    () =>
      quizQuestions.filter(
        (question) => submittedAnswers[question.id] !== question.correctValue
      ).map((question) => question.id),
    [quizQuestions, submittedAnswers]
  );
  const allQuestionsAnswered = useMemo(
    () =>
      quizQuestions.every(
        (question) => typeof submittedAnswers[question.id] === "string"
      ),
    [quizQuestions, submittedAnswers]
  );
  const passedQuiz = attemptedQuiz && incorrectQuestionIds.length === 0;
  const trackingParams = getTrackingRulesParams(design, 2);

  function handleContinueToQuiz() {
    setCurrentPage("quiz");
    player.stage.set("stage2RulesPage", "quiz");
  }

  function handleBackToRules() {
    setCurrentPage("rules");
    player.stage.set("stage2RulesPage", "rules");
  }

  function handleAnswerChange(questionId, value) {
    const nextAnswers = {
      ...answers,
      [questionId]: value,
    };
    setAnswers(nextAnswers);
    player.stage.set("stage2RulesQuizAnswers", nextAnswers);
  }

  function handleSubmitQuiz() {
    const nextCount = (player.stage.get("stage2RulesQuizAttemptCount") ?? 0) + 1;
    setSubmittedAnswers(answers);
    setAttemptedQuiz(true);
    // Keep repeated submissions distinct in Empirica's attribute history.
    player.stage.set("stage2RulesQuizSubmittedAnswers", { ...answers, _attempt: nextCount });
    player.stage.set("stage2RulesQuizAttempted", true);
    player.stage.set("stage2RulesQuizAttemptCount", nextCount);
  }

  function handleContinue() {
    player.stage.set("submit", true);
  }

  return (
    <div className="py-10 max-w-4xl mx-auto px-8 space-y-8">
      {currentPage === "rules" ? (
        <>
          <h4 className="text-4xl font-semibold text-gray-900">Game 2</h4>
          <div className="rounded-md border border-gray-200 p-5 space-y-3 text-2xl text-gray-700">
            <p>
              You will play another tracking game for <b>{trackingParams.durationSeconds} seconds</b>.
            </p>
            
            {lossFrame ? (
              <>
                <p>
                  At the start of Game 2, <b>${formatDollars(trackingParams.maxPerParticipant)}</b>{" "}
                  is at risk. You <b><u>lose</u> {formatCents(trackingParams.centsPerSecond)} cents</b> each second{" "}
                  your <b>cursor is <u>outside</u> the circle</b>.
                </p>
                <div>
                  <b><u>NEW:</u> You and your {counterpart.term} will <u>negotiate</u> how to split the savings between you.</b>
                </div>
              </>
            ) : (
              <div><b><u>NEW:</u> You and your {counterpart.term} will <u>negotiate</u> how to split the pot</b>.</div>
            )}
          
            {showStandardDifficultyUpdate ? (
              <p>
                <b><u>NEW:</u></b> <b>Both players</b> will have{" "}
                <b>standard difficulty</b> (<b><u>the same size circle</u></b>).
              </p>
            ) : null}

          </div>

          <div className="flex items-center space-x-4">
            <Button
              handleClick={handleContinueToQuiz}
              className="px-8 py-4 text-2xl"
              autoFocus
              disabled={player.stage.get("submit")}
            >
              Continue to Quiz
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="rounded-md border border-gray-200 p-5 space-y-6 text-[19px] text-gray-700">
            <div className="space-y-2">
              <h2 className="text-[23px] font-medium text-gray-900">
                Game 2 Rules Check
              </h2>
              <p>
                Answer all questions correctly to confirm you understand how
                Game 2 works.
              </p>
            </div>

            <div className="space-y-6">
              {quizQuestions.map((question, index) => {
                const selectedValue = answers[question.id];
                const submittedValue = submittedAnswers[question.id];
                const showIncorrect =
                  attemptedQuiz &&
                  typeof submittedValue === "string" &&
                  submittedValue !== question.correctValue;
                const showCorrect =
                  attemptedQuiz && submittedValue === question.correctValue;

                return (
                  <div
                    key={question.id}
                    className={`rounded-md border p-4 space-y-3 ${
                      showIncorrect
                        ? "border-red-300 bg-red-50"
                        : showCorrect
                          ? "border-green-300 bg-green-50"
                          : "border-gray-200"
                    }`}
                  >
                    <p className="font-medium text-gray-900">
                      {index + 1}. {question.prompt}
                    </p>
                    <div className="space-y-2">
                      {question.options.map((option) => (
                        <label
                          key={option.value}
                          className="flex items-center space-x-3 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name={question.id}
                            value={option.value}
                            checked={selectedValue === option.value}
                            onChange={() =>
                              handleAnswerChange(question.id, option.value)
                            }
                            className="h-4 w-4 shrink-0"
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                    {showIncorrect ? (
                      <p className="text-[19px] text-red-600">
                        This answer is not correct. Re-read the rules and try again.
                      </p>
                    ) : null}
                    {showCorrect ? (
                      <p className="text-[19px] text-green-700">Correct.</p>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {attemptedQuiz && !passedQuiz ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-[19px] text-red-700">
                {allQuestionsAnswered
                  ? "Some answers are still incorrect. Update your choices and submit the quiz again."
                  : "Answer every question, then submit the quiz again."}
              </div>
            ) : null}

            {passedQuiz ? (
              <div className="rounded-md border border-green-200 bg-green-50 p-4 text-[19px] text-green-700">
                All answers are correct. You can continue.
              </div>
            ) : null}
          </div>

          <div className="flex items-center space-x-4">
            <Button handleClick={handleBackToRules}>
              <span className="text-[17px]">Back to Rules</span>
            </Button>
            <Button
              handleClick={handleSubmitQuiz}
              disabled={player.stage.get("submit")}
            >
              <span className="text-[17px]">Submit Quiz</span>
            </Button>
            <Button
              handleClick={handleContinue}
              autoFocus
              disabled={player.stage.get("submit") || !passedQuiz}
            >
              <span className="text-[17px]">Continue</span>
            </Button>
            {player.stage.get("submit") && !allSubmitted ? (
              <span className="text-[17px] text-gray-500">
                Waiting for {counterpart.term}…
              </span>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
